<template>
  <div class="rtb-dashboard__rtb-log-detail">
    <Tabs :transition-duration="0">
      <Tab
        :title="$t('TAB_HEAD_BID_DETAILS')"
      >
        <RtbData
          :bid-record="bidRecord"
          :time-zone-id="timeZoneId"
        />
      </Tab>
      <Tab
        :title="$t('TAB_HEAD_BID_TRACE')"
      >
        <RtbBidTrace :bid-trace="bidTrace" />
      </Tab>
      <Tab
        :title="$t('TAB_HEAD_BID_EVENTS')"
      >
        <RtbEvents :parsed-events="mainEvents" />
      </Tab>
      <Tab
        v-if="hasRingTreeEvents"
        :title="$t('TAB_HEAD_BID_RING_TREE_EVENTS')"
      >
        <RtbEvents :parsed-events="ringTreeEvents" />
      </Tab>
      <Tab
        :title="$t('TAB_HEAD_BID_TAGS')"
      >
        <RtbTags :grouped-tags="groupedTags" />
      </Tab>
    </Tabs>
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import 'moment-timezone';
import messages from '../../../locales';
import sharedMessages from '@/core/locales';

import eventsMapping from './eventsMapping';
import { eventFieldFormatters } from '@/modules/reporting/components/AgGridUtils';
import RtbData from './RtbData';
import RtbBidTrace from './RtbBidTrace';
import RtbTags from './RtbTags';
import RtbEvents from './RtbEvents';

const excludeEventKeys = {
  'dtStamp': true, 
  'id': true,
  name: true,
  eventStrVals: true,
  eventVals: true
};

export default {
  name: 'RtbRecordDetails',
  components: {
    RtbData,
    RtbBidTrace,
    RtbTags,
    RtbEvents
  },
  props: {
    bidRecord: {
      type: Object,
      default: () => {
        return {};
      }
    },
    timeZoneId: {
      type: String,
      default() {
        return moment.tz.guess();
      }
    },
    role: {
      type: String,
      default: 'admin'
    }
  },
  data() {
    return {
      parsedEvents: [],
      ringTreeEventNames: Object.freeze({
        'PingRAWResult': true,
        'PingSuccess': true,
        'PingFailure': true,
        'SecondPingRAWResult': true,
        'SecondPingSuccess': true,
        'SecondPingFailure': true,
      }),
    }
  },
  watch: {
    timeZoneId: {
      handler() {
        this.parsedEvents = this.parseEvents(this.bidRecord.events);
      },
      immediate: true
    }
  },
  mounted() {
    this.parsedEvents = this.parseEvents(this.bidRecord.events);
  },
  computed: {
    bidTrace() {
      return this.bidRecord?.trace ? JSON.parse(this.bidRecord?.trace) : {};
    },
    groupedTags() {
      if(!this.bidRecord['message-tags']) {
        return {};
      }
      return this.bidRecord['message-tags'].reduce((finalValue, tag) => {
        finalValue[tag.type] = finalValue[tag.type] || [];

        finalValue[tag.type].push(tag);

        return finalValue;
      }, {});
    },
    hasRingTreeEvents() {
      for (let i = 0; i < this.parsedEvents.length; i++)
      {
        if (this.ringTreeEventNames[this.parsedEvents[i].name]) {
          return true;
        }        
      } 
      return false;
    },
    mainEvents() {
      return this.parsedEvents.filter((e) => !this.ringTreeEventNames[e.name]);
    },
    ringTreeEvents() {
      return this.parsedEvents.filter((e) => this.ringTreeEventNames[e.name]);      
    },
  },
  methods: {
    parseEvents(events) {
      const parsedEvents = [];
      events.reduce((parsedEvents, event) => {
        const eventMetadata = eventsMapping[event.name];
        let displayName = '';

        if (eventMetadata) {
          const columns = [];
          displayName = eventMetadata['DisplayName'] ? eventMetadata['DisplayName'] : Vue.filter('camelCaseFormat')(event.name);

          if (eventMetadata['DisplayNameForRoles']?.[this.role]) {
            displayName = eventMetadata['DisplayNameForRoles'][this.role];
          }

          event.eventVals?.forEach(eventVal => {
            const fieldMetadata = eventMetadata.Fields[eventVal.name];

            if (eventVal.value !== '') {
              columns.push({
                name: eventVal.name,
                displayName: fieldMetadata?.Field['DisplayName'] || Vue.filter('camelCaseFormat')(eventVal.name),
                formattedValue: (eventFieldFormatters[fieldMetadata?.Field?.FieldType] && eventFieldFormatters[fieldMetadata?.Field?.FieldType](eventVal.value, this.timeZoneId)) 
                              || eventVal.value
              });
            }
          });

          event.eventStrVals?.forEach(eventVal => {
            const fieldMetadata = eventMetadata.Fields[eventVal.name];

            if (eventVal.value !== '') {
              columns.push({
                name: eventVal.name,
                displayName: fieldMetadata?.Field['DisplayName'] || Vue.filter('camelCaseFormat')(eventVal.name),
                formattedValue: (eventFieldFormatters[fieldMetadata?.Field?.FieldType] && eventFieldFormatters[fieldMetadata.Field.FieldType](eventVal.value))
                              || eventVal.value
              });
            }
          });

          for (const key in event) {
            if (!excludeEventKeys[key] && event[key] !== '') {
              const fieldMetadata = eventMetadata.Fields[key];

              columns.push({
                name: key,
                weight: fieldMetadata?.FieldWeight,
                displayName: fieldMetadata?.Field['DisplayNameForRoles']?.[this.role] || fieldMetadata?.Field['DisplayName'] || Vue.filter('camelCaseFormat')(key),
                formattedValue: (eventFieldFormatters[fieldMetadata?.Field?.FieldType] && eventFieldFormatters[fieldMetadata.Field.FieldType](event[key], this.timeZoneId))
                                || event[key]
              });
            }
          }

          columns.sort((columnA, columnB) => {

            if (columnA.weight && columnB.weight) {
              if(columnA.weight < columnB.weight) {
                return -1; 
              }
              if(columnA.weight > columnB.weight) {
                return 1;
              }
            } else {
              if(columnA.displayName < columnB.displayName) { return -1; }
              if(columnA.displayName > columnB.displayName) { return 1; }
            }
            return 0;
          });

          parsedEvents.push({
            name: event.name,
            displayName,
            dtStamp: event.dtStamp,
            dateFormatted: eventFieldFormatters['date'](event.dtStamp, this.timeZoneId),
            columns
          });
        }

        return parsedEvents;
      }, parsedEvents);

      return parsedEvents.sort((eventA, eventB) => {
        const eventAEpoch = moment(eventA.dtStamp).valueOf();
        const eventBEpoch = moment(eventB.dtStamp).valueOf();

        return eventAEpoch - eventBEpoch;
      });
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>
<style lang="scss" scoped>
  .table_basic {
    th {
      width: 175px;
    }
    .hidden-row {
     & > td {
        padding: 0;
        border-bottom: 0;
      }
      &.expanded {
       & > td {
          border-bottom: 1px solid var(--component-form__input-bg-default);
        }
      }
    }
  }
  .fa-angle-right {
    transition: transform 0.2s ease;
  }
</style>